import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ListChecks, Pencil, Plus, Send, Trash2, Undo2, Users } from 'lucide-react';
import {
  LmsQuizListItem,
  LmsQuizSettings,
  LmsQuizStatus,
  LmsQuizzesApi,
} from '@/services/lms.api';
import { DataTable } from '@/components/ui/DataTable';
import { Pagination } from '@/components/ui/Pagination';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select, Textarea, Checkbox } from '@/components/ui/Input';
import { toast } from '@/stores/toast.store';
import { usePermissions } from '@/hooks/usePermissions';
import { LmsQuizQuestionsModal } from './LmsQuizQuestionsModal';
import { LmsQuizAttemptsModal } from './LmsQuizAttemptsModal';

const statusTone: Record<LmsQuizStatus, 'slate' | 'green'> = { draft: 'slate', published: 'green' };

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/** Compact, omits-unset summary — matches the FIXED SPEC's own example
 * ("30 min · 2 attempts · pass 60%"): only the three settings that example
 * shows, not every settings key (shuffle/showResults aren't glanceable
 * table material — they're in the edit modal). */
function settingsSummary(s: LmsQuizSettings): string {
  const parts: string[] = [];
  if (s.timeLimitMinutes) parts.push(`${s.timeLimitMinutes} min`);
  if (s.attemptsAllowed != null) parts.push(`${s.attemptsAllowed} attempt${s.attemptsAllowed === 1 ? '' : 's'}`);
  if (s.passMarkPercent != null) parts.push(`pass ${s.passMarkPercent}%`);
  return parts.length ? parts.join(' · ') : 'Default settings';
}

const schema = z
  .object({
    title: z.string().min(1, 'Required').max(200),
    description: z.string().optional().or(z.literal('')),
    timeLimitMinutes: z.string().optional().or(z.literal('')),
    attemptsAllowed: z.string().optional().or(z.literal('')),
    shuffleQuestions: z.boolean(),
    shuffleOptions: z.boolean(),
    showResults: z.enum(['immediately', 'after_close']),
    passMarkPercent: z.string().optional().or(z.literal('')),
  })
  .superRefine((v, ctx) => {
    if (v.timeLimitMinutes?.trim()) {
      const n = Number(v.timeLimitMinutes);
      if (!Number.isInteger(n) || n <= 0) {
        ctx.addIssue({ path: ['timeLimitMinutes'], code: z.ZodIssueCode.custom, message: 'Must be a positive whole number' });
      }
    }
    if (v.attemptsAllowed?.trim()) {
      const n = Number(v.attemptsAllowed);
      if (!Number.isInteger(n) || n <= 0) {
        ctx.addIssue({ path: ['attemptsAllowed'], code: z.ZodIssueCode.custom, message: 'Must be a positive whole number' });
      }
    }
    if (v.passMarkPercent?.trim()) {
      const n = Number(v.passMarkPercent);
      if (!Number.isInteger(n) || n < 0 || n > 100) {
        ctx.addIssue({ path: ['passMarkPercent'], code: z.ZodIssueCode.custom, message: 'Must be an integer between 0 and 100' });
      }
    }
  });
type FormValues = z.infer<typeof schema>;

/** All three of these now share the same "blank = explicit null = clear"
 * convention (validateQuizSettingsShape accepts null for all three) — a
 * blank field always sends null rather than omitting the key, so editing
 * a quiz can actually unset a previously-set value, not just leave it. */
function buildSettingsPayload(v: FormValues): LmsQuizSettings {
  return {
    shuffleQuestions: v.shuffleQuestions,
    shuffleOptions: v.shuffleOptions,
    showResults: v.showResults,
    attemptsAllowed: v.attemptsAllowed?.trim() ? Number(v.attemptsAllowed) : null,
    timeLimitMinutes: v.timeLimitMinutes?.trim() ? Number(v.timeLimitMinutes) : null,
    passMarkPercent: v.passMarkPercent?.trim() ? Number(v.passMarkPercent) : null,
  };
}

export function LmsQuizzesTab({ courseId }: { courseId: string }) {
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [modal, setModal] = useState<{ open: boolean; quiz?: LmsQuizListItem }>({ open: false });
  const [confirmDelete, setConfirmDelete] = useState<LmsQuizListItem | null>(null);
  const [questionsFor, setQuestionsFor] = useState<LmsQuizListItem | null>(null);
  const [attemptsFor, setAttemptsFor] = useState<LmsQuizListItem | null>(null);

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['lms-quizzes', courseId, page, limit],
    queryFn: () => LmsQuizzesApi.list(courseId, { page, limit }),
    placeholderData: keepPreviousData,
  });

  // Not scoped to a single quizId — the Questions modal's own
  // ['lms-quiz-detail', quizId] query must go stale on any of these
  // mutations (publish/unpublish flip its read-only lock; settings edits
  // change what it shows), regardless of which surface (this table or the
  // modal's own unpublish button) triggered the change.
  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['lms-quizzes', courseId] });
    qc.invalidateQueries({ queryKey: ['lms-quiz-detail'] });
  };

  const upsert = useMutation({
    mutationFn: (v: { id?: string; payload: ReturnType<typeof buildPayload> }) =>
      v.id ? LmsQuizzesApi.update(v.id, v.payload) : LmsQuizzesApi.create(courseId, v.payload),
    onSuccess: () => {
      invalidateAll();
      setModal({ open: false });
      toast.success('Quiz saved');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Save failed'),
  });

  const publish = useMutation({
    mutationFn: (id: string) => LmsQuizzesApi.publish(id),
    onSuccess: () => {
      invalidateAll();
      toast.success('Quiz published');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Publish failed'),
  });

  const unpublish = useMutation({
    mutationFn: (id: string) => LmsQuizzesApi.unpublish(id),
    onSuccess: () => {
      invalidateAll();
      toast.success('Quiz unpublished');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Unpublish failed'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => LmsQuizzesApi.remove(id),
    onSuccess: () => {
      invalidateAll();
      setConfirmDelete(null);
      toast.success('Quiz deleted');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Delete failed'),
  });

  const rows = pageData?.items ?? [];

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500">Quizzes for this course.</p>
        <button className="btn-primary" onClick={() => setModal({ open: true })}>
          <Plus className="mr-1.5 h-4 w-4" /> New quiz
        </button>
      </div>

      <DataTable<LmsQuizListItem>
        rows={rows}
        getRowId={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No quizzes yet."
        columns={[
          {
            key: 'title',
            header: 'Title',
            render: (z) => (
              <button
                className="font-medium text-slate-900 hover:text-brand-700 hover:underline"
                onClick={() => setQuestionsFor(z)}
              >
                {z.title}
              </button>
            ),
          },
          { key: 'status', header: 'Status', render: (z) => <Badge tone={statusTone[z.status]}>{z.status}</Badge> },
          { key: 'questionCount', header: 'Questions', render: (z) => z.questionCount },
          { key: 'totalMarks', header: 'Marks', render: (z) => z.totalMarks },
          { key: 'settings', header: 'Settings', render: (z) => settingsSummary(z.settings) },
        ]}
        actions={(z) => (
          <>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
              onClick={() => setQuestionsFor(z)}
              title="Questions"
            >
              <ListChecks className="h-4 w-4" />
            </button>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
              onClick={() => setAttemptsFor(z)}
              title="Attempts"
            >
              <Users className="h-4 w-4" />
            </button>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
              onClick={() => setModal({ open: true, quiz: z })}
              title="Edit settings"
            >
              <Pencil className="h-4 w-4" />
            </button>
            {z.status === 'draft' ? (
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-green-600 disabled:opacity-40"
                onClick={() => publish.mutate(z.id)}
                disabled={publish.isPending}
                title="Publish"
              >
                <Send className="h-4 w-4" />
              </button>
            ) : (
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-amber-600 disabled:opacity-40"
                onClick={() => unpublish.mutate(z.id)}
                disabled={unpublish.isPending}
                title="Unpublish"
              >
                <Undo2 className="h-4 w-4" />
              </button>
            )}
            {isAdmin && (
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                onClick={() => setConfirmDelete(z)}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </>
        )}
      />

      {pageData && (
        <Pagination
          page={pageData.page}
          totalPages={pageData.totalPages}
          total={pageData.total}
          limit={pageData.limit}
          onPageChange={setPage}
          onLimitChange={(l) => {
            setPage(1);
            setLimit(l);
          }}
        />
      )}

      {modal.open && (
        <QuizSettingsFormModal
          open={modal.open}
          quiz={modal.quiz}
          onClose={() => setModal({ open: false })}
          saving={upsert.isPending}
          onSubmit={(v) => upsert.mutate({ id: modal.quiz?.id, payload: buildPayload(v) })}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.id)}
        loading={remove.isPending}
        title="Delete quiz?"
        message={`Soft-delete "${confirmDelete?.title}" and its ${confirmDelete?.questionCount ?? 0} question(s). Existing attempts remain in the database.`}
        confirmText="Delete quiz"
      />

      {questionsFor && (
        <LmsQuizQuestionsModal quizId={questionsFor.id} quizTitle={questionsFor.title} onClose={() => setQuestionsFor(null)} />
      )}

      {attemptsFor && (
        <LmsQuizAttemptsModal quiz={attemptsFor} onClose={() => setAttemptsFor(null)} />
      )}
    </>
  );
}

function buildPayload(v: FormValues) {
  return {
    title: v.title,
    description: v.description?.trim() ? v.description : undefined,
    settings: buildSettingsPayload(v),
  };
}

function settingsToForm(s?: LmsQuizSettings): Omit<FormValues, 'title' | 'description'> {
  return {
    timeLimitMinutes: s?.timeLimitMinutes != null ? String(s.timeLimitMinutes) : '',
    attemptsAllowed: s?.attemptsAllowed != null ? String(s.attemptsAllowed) : '',
    shuffleQuestions: s?.shuffleQuestions ?? false,
    shuffleOptions: s?.shuffleOptions ?? false,
    showResults: s?.showResults ?? 'after_close',
    passMarkPercent: s?.passMarkPercent != null ? String(s.passMarkPercent) : '',
  };
}

function QuizSettingsFormModal({
  open,
  quiz,
  onClose,
  onSubmit,
  saving,
}: {
  open: boolean;
  quiz?: LmsQuizListItem;
  onClose: () => void;
  onSubmit: (v: FormValues) => void;
  saving: boolean;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      title: quiz?.title ?? '',
      description: quiz?.description ?? '',
      ...settingsToForm(quiz?.settings),
    },
  });

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={quiz ? `Edit ${quiz.title}` : 'New quiz'}
      size="md"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit(onSubmit)} disabled={saving}>
            {saving ? 'Saving…' : quiz ? 'Save changes' : 'Create quiz'}
          </button>
        </>
      }
    >
      <form className="space-y-4">
        <Field label="Title" required error={errors.title?.message}>
          <Input {...register('title')} placeholder="Chapter 1 Quiz" />
        </Field>
        <Field label="Description" hint="Optional">
          <Textarea rows={3} {...register('description')} placeholder="What this quiz covers…" />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Time limit (minutes)"
            error={errors.timeLimitMinutes?.message}
            hint="Optional — leave blank for no limit"
          >
            <Input type="number" min={1} {...register('timeLimitMinutes')} placeholder="e.g. 30" />
          </Field>
          <Field
            label="Attempts allowed"
            error={errors.attemptsAllowed?.message}
            hint="Optional — leave blank for unlimited"
          >
            <Input type="number" min={1} {...register('attemptsAllowed')} placeholder="e.g. 2" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Show results to students" required>
            <Select {...register('showResults')}>
              <option value="immediately">Immediately</option>
              <option value="after_close">Never shown to students</option>
            </Select>
          </Field>
          <Field label="Pass mark %" error={errors.passMarkPercent?.message} hint="Optional, 0–100">
            <Input type="number" min={0} max={100} {...register('passMarkPercent')} placeholder="e.g. 60" />
          </Field>
        </div>
        <div className="space-y-2">
          <Checkbox label="Shuffle question order per attempt" {...register('shuffleQuestions')} />
          <Checkbox label="Shuffle option order per attempt" {...register('shuffleOptions')} />
        </div>
      </form>
    </Modal>
  );
}
